/**
 * Admin API Service
 * Handles all admin-related API calls for onboarding, user management, etc.
 */

import axios, { AxiosError } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

// Types
export interface OnboardingApplication {
  id: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'DENIED';
  store_name: string;
  llc_name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  zip_code: string;
  full_name: string;
  contact_phone: string;
  email: string;
  bank_name: string;
  account_type: 'checking' | 'savings';
  routing_number: string;
  signature_data_url?: string;
  terms_accepted_at: string;
  tax_id_document_path?: string;
  federal_id_document_path?: string;
  tobacco_license_document_path?: string;
  // Base64 document fields (used by the backend since migration 015)
  tax_id_document_base64?: string;
  tax_id_document_filename?: string;
  federal_id_document_base64?: string;
  federal_id_document_filename?: string;
  tobacco_license_document_base64?: string;
  tobacco_license_document_filename?: string;
  created_at: string;
  updated_at: string;
  reviewed_by_user_id?: number;
  reviewed_at?: string;
  denial_reason?: string;
}

export interface PaginatedApplicationsResponse {
  data: OnboardingApplication[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApprovalResponse {
  success: boolean;
  message: string;
  user: { id: number; email: string; full_name: string; role: string; created_at: string };
  profile: Record<string, unknown>;
  // application field kept for backwards compatibility with reject response shape
  application?: OnboardingApplication;
}

export interface RejectionResponse {
  success: boolean;
  message: string;
  application: OnboardingApplication;
}

export interface ApplicationFilters {
  page?: number;
  limit?: number;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'DENIED' | 'all';
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
}

export interface DocumentResponse {
  data: Buffer;
  contentType: string;
  filename: string;
}

export class AdminApiError extends Error {
  constructor(
    message: string,
    public statusCode: number = 500,
    public field?: string
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

class AdminApiService {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private getHeaders(): Record<string, string> {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private async handleError(error: unknown, context: string): Promise<never> {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.message ||
        axiosError.message ||
        `${context} failed`;
      const statusCode = axiosError.response?.status || 500;

      throw new AdminApiError(message, statusCode);
    }

    throw new AdminApiError(`${context} failed: ${String(error)}`, 500);
  }

  /**
   * Fetch paginated list of onboarding applications
   */
  async getOnboardingApplications(
    filters?: ApplicationFilters
  ): Promise<PaginatedApplicationsResponse> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        if (filters.page) params.append('page', String(filters.page));
        if (filters.limit) params.append('limit', String(filters.limit));
        if (filters.status && filters.status !== 'all') {
          // Backend uses DENIED, frontend uses REJECTED - normalize for the API call
          const backendStatus = filters.status === 'REJECTED' ? 'DENIED' : filters.status;
          params.append('status', backendStatus);
        }
        if (filters.search) params.append('search', filters.search);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
      }

      const queryString = params.toString();
      const url =
        `${this.baseURL}/onboarding/admin/applications` +
        (queryString ? `?${queryString}` : '');

      const response = await axios.get<PaginatedApplicationsResponse>(url, {
        headers: this.getHeaders(),
      });

      return response.data;
    } catch (error) {
      return this.handleError(error, 'Fetch onboarding applications');
    }
  }

  /**
   * Get single onboarding application details
   */
  async getApplication(applicationId: number): Promise<OnboardingApplication> {
    try {
      const response = await axios.get<{
        data: OnboardingApplication;
      }>(
        `${this.baseURL}/onboarding/admin/applications/${applicationId}`,
        { headers: this.getHeaders() }
      );

      return response.data.data;
    } catch (error) {
      return this.handleError(error, `Fetch application ${applicationId}`);
    }
  }

  /**
   * Approve an onboarding application
   */
  async approveApplication(
    applicationId: number,
    notes?: string
  ): Promise<ApprovalResponse> {
    try {
      const response = await axios.patch<ApprovalResponse>(
        `${this.baseURL}/onboarding/admin/applications/${applicationId}/approve`,
        { notes: notes || '' },
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error) {
      return this.handleError(error, `Approve application ${applicationId}`);
    }
  }

  /**
   * Reject an onboarding application
   */
  async rejectApplication(
    applicationId: number,
    reason: string,
    notes?: string
  ): Promise<RejectionResponse> {
    try {
      const response = await axios.patch<RejectionResponse>(
        `${this.baseURL}/onboarding/admin/applications/${applicationId}/deny`,
        {
          reason,
          notes: notes || '',
        },
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error) {
      return this.handleError(error, `Reject application ${applicationId}`);
    }
  }

  /**
   * Download a document from an application
   */
  async downloadDocument(documentPath: string): Promise<string> {
    try {
      const response = await axios.get(
        `${this.baseURL}/onboarding/documents`,
        {
          params: { path: documentPath },
          headers: this.getHeaders(),
          responseType: 'blob',
        }
      );

      // Create a blob URL and return it
      return window.URL.createObjectURL(response.data);
    } catch (error) {
      return this.handleError(error, `Download document ${documentPath}`);
    }
  }

  /**
   * Get document preview URL
   */
  getDocumentPreviewUrl(documentPath: string): string {
    const token = localStorage.getItem('accessToken');
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${this.baseURL}/onboarding/documents/preview${tokenParam}&path=${encodeURIComponent(documentPath)}`;
  }

  /**
   * Check if user has admin permissions
   */
  async checkAdminPermissions(): Promise<boolean> {
    try {
      await axios.get(`${this.baseURL}/onboarding/admin/permissions/check`, {
        headers: this.getHeaders(),
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<{
    pendingCount: number;
    approvedCount: number;
    rejectedCount: number;
    averageReviewTime: number;
  }> {
    try {
      const response = await axios.get<{
        data: {
          pendingCount: number;
          approvedCount: number;
          rejectedCount: number;
          averageReviewTime: number;
        };
      }>(`${this.baseURL}/onboarding/admin/stats`, {
        headers: this.getHeaders(),
      });

      return response.data.data;
    } catch (error) {
      return this.handleError(error, 'Fetch dashboard statistics');
    }
  }
}

export const adminApi = new AdminApiService();
